/**
 * 🎭 EmotionBreakdownChart — Feature Premium (Emotion Analytics)
 *
 * Donut com a distribuição de emoções do período (feliz, triste, raiva,
 * neutro, surpresa, medo, nojo, desprezo) + percentual de rostos não
 * classificados (unknown).
 *
 * Só é renderizado no dashboard quando a organização tem a feature
 * habilitada (emotion_analytics_enabled=TRUE). Sem flag → nada muda
 * no sistema (componente nunca monta).
 *
 * Zero `any`.
 */

import React from 'react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
} from 'recharts';
import type { PieLabelRenderProps } from 'recharts';
import { EmotionStats } from '../../domain/entities/Analytics';

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface EmotionBreakdownChartProps {
  data: EmotionStats;
}

interface EmotionChartPoint {
  name: string;
  value: number;
  percentage: number;
  [key: string]: string | number;
}

// ─── Constantes ────────────────────────────────────────────────────────────────

const EMOTION_META: { key: string; label: string; color: string }[] = [
  { key: 'happy',     label: 'Feliz',     color: '#22c55e' }, // verde
  { key: 'neutral',   label: 'Neutro',    color: '#94a3b8' }, // cinza
  { key: 'sad',       label: 'Triste',    color: '#3b82f6' }, // azul
  { key: 'angry',     label: 'Raiva',     color: '#ef4444' }, // vermelho
  { key: 'surprise',  label: 'Surpreso',  color: '#eab308' }, // amarelo
  { key: 'fear',      label: 'Medo',      color: '#8b5cf6' }, // roxo
  { key: 'disgust',   label: 'Nojo',      color: '#b45309' }, // âmbar escuro
  { key: 'contempt',  label: 'Desprezo',  color: '#14b8a6' }, // teal
];

// ─── Helpers ───────────────────────────────────────────────────────────────────

const renderPieLabel = (props: PieLabelRenderProps): React.ReactElement | null => {
  const RADIAN = Math.PI / 180;
  const cx        = Number(props.cx        ?? 0);
  const cy        = Number(props.cy        ?? 0);
  const midAngle  = Number(props.midAngle  ?? 0);
  const innerR    = Number(props.innerRadius ?? 0);
  const outerR    = Number(props.outerRadius ?? 0);
  const percent   = Number(props.percent   ?? 0);
  if (percent < 0.04) return null; // fatia muito pequena — pula
  const r = innerR + (outerR - innerR) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central"
      fontSize={12} fontWeight="bold" style={{ pointerEvents: 'none' }}>
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────────

export const EmotionBreakdownChart: React.FC<EmotionBreakdownChartProps> = ({
  data,
}) => {
  const chartData: EmotionChartPoint[] = EMOTION_META.map((meta) => {
    const dist = data.distribution[meta.key as keyof EmotionStats['distribution']];
    const count = dist ? dist.count : 0;
    const pct = dist ? dist.percentage : 0;
    return { name: meta.label, value: count, percentage: pct };
  }).filter((p) => p.value > 0);

  const unknownDist = data.distribution.unknown;
  const unknownCount = unknownDist ? unknownDist.count : 0;
  const unknownPct = unknownDist ? unknownDist.percentage : 0;

  const totalClassified = data.total_classified;

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-2 mb-1">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
          Análise de Emoções
        </h3>
        <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 font-medium uppercase tracking-wide">
          Premium
        </span>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
        Estimativa estatística do humor dos visitantes (expressões faciais agregadas)
      </p>

      {totalClassified === 0 && unknownCount === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-gray-500">
          <p className="text-sm">Sem dados de emoções no período selecionado</p>
          <p className="text-xs mt-1">Os dados começam a ser coletados após a ativação da feature</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ── Donut ── */}
            <div>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderPieLabel}
                    outerRadius={95}
                    dataKey="value"
                  >
                    {chartData.map((point) => {
                      const meta = EMOTION_META.find((m) => m.label === point.name);
                      return (
                        <Cell
                          key={`cell-${point.name}`}
                          fill={meta ? meta.color : '#94a3b8'}
                        />
                      );
                    })}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      const point = chartData.find((p) => p.name === name);
                      return [
                        `${value.toLocaleString('pt-BR')} pessoas (${point?.percentage.toFixed(1) ?? '0'}%)`,
                        name,
                      ] as [string, string];
                    }}
                    contentStyle={{
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      border: '1px solid #ccc',
                      borderRadius: '8px',
                    }}
                    wrapperClassName="dark:text-gray-900"
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Legenda manual (mantém no dark mode) */}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-1.5 mt-1">
                {chartData.map((d) => {
                  const meta = EMOTION_META.find((m) => m.label === d.name);
                  return (
                    <div key={d.name} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: meta ? meta.color : '#94a3b8' }} />
                      {d.name}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Lista com contagens ── */}
            <div className="flex flex-col justify-center gap-2">
              {EMOTION_META.map((meta) => {
                const dist = data.distribution[meta.key as keyof EmotionStats['distribution']];
                const count = dist ? dist.count : 0;
                const pct = dist ? dist.percentage : 0;
                return (
                  <div key={meta.key} className="flex items-center gap-3">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
                    <span className="text-sm text-gray-700 dark:text-gray-200 w-20">{meta.label}</span>
                    <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.min(pct, 100)}%`, backgroundColor: meta.color }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-100 w-24 text-right">
                      {count.toLocaleString('pt-BR')} ({pct.toFixed(1)}%)
                    </span>
                  </div>
                );
              })}

              {unknownCount > 0 && (
                <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                  {unknownCount.toLocaleString('pt-BR')} rostos sem leitura clara
                  ({unknownPct.toFixed(1)}%) — não contabilizados
                </p>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
